import { parse as parseYaml } from "yaml";
import type { ConnectorRequirement, InputField, SkillRequirements } from "./types.js";

/**
 * Extract YAML frontmatter from a SKILL.md string.
 *
 * Frontmatter is delimited by leading `---` fences. Returns the parsed object,
 * or an empty object `{}` if no frontmatter block is found or if the YAML is malformed.
 * Errors are silently swallowed — callers should treat an empty result as "no requirements".
 *
 * @param content - Raw SKILL.md file content (full string, including `---` fences)
 * @returns Parsed frontmatter object, or `{}` on absence or parse failure
 *
 * @docLink packages/resolver/api-reference#parseskill-frontmatter
 */
export function parseSkillFrontmatter(content: string): Record<string, any> {
  const match = content.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return {};
  try {
    return parseYaml(match[1]!) ?? {};
  } catch {
    return {};
  }
}

/**
 * Parse skill requirements from raw SKILL.md content into a `SkillRequirements` object.
 *
 * Supports two frontmatter formats:
 * 1. **New format** — `metadata.prerequisites` with explicit
 *    `files`, `inputs_required`, `inputs_optional`, `reads`, `produces`, and `resources` arrays.
 * 2. **Legacy format** — `metadata.user_inputs.dialog[]` array, automatically promoted to
 *    `inputs_required` / `inputs_optional` based on each entry's `required` flag.
 *
 * Returns empty requirements `{ files: [], inputs_required: [], inputs_optional: [], reads: [], produces: [], resources: [] }`
 * when no recognisable prerequisites section is found.
 *
 * @param content - Raw SKILL.md file content (full string, including `---` fences)
 * @returns Structured `SkillRequirements` (never throws)
 *
 * @docLink packages/resolver/api-reference#parseskill-requirements
 */
export function parseSkillRequirements(content: string): SkillRequirements {
  const fm = parseSkillFrontmatter(content);
  const meta = fm.metadata ?? {};
  const prerequisites = meta.prerequisites ?? {};

  const empty: SkillRequirements = {
    files: [],
    inputs_required: [],
    inputs_optional: [],
    reads: [],
    produces: [],
    resources: [],
    connectors: [],
  };

  // New format: metadata.prerequisites
  if (
    prerequisites.files ||
    prerequisites.inputs_required ||
    prerequisites.inputs_optional ||
    prerequisites.reads ||
    prerequisites.produces ||
    prerequisites.resources ||
    prerequisites.connectors
  ) {
    return {
      files: (prerequisites.files ?? []).map((f: any) => ({
        path: f.path,
        gate: f.gate ?? "hard",
        description: f.description ?? "",
        ...(f.min_entries != null ? { min_entries: f.min_entries } : {}),
      })),
      inputs_required: (prerequisites.inputs_required ?? []).map(normalizeInputField(true)),
      inputs_optional: (prerequisites.inputs_optional ?? []).map(normalizeInputField(false)),
      reads: (prerequisites.reads ?? []).map((r: any) => ({
        path: r.path,
        description: r.description ?? "",
      })),
      produces: (prerequisites.produces ?? []).map((p: any) => ({
        path: p.path,
        description: p.description ?? "",
      })),
      resources: (prerequisites.resources ?? []).map((r: any) => ({
        id: r.id,
        ...(r.operations ? { operations: r.operations } : {}),
        optional: r.optional ?? false,
        ...(r.description ? { description: r.description } : {}),
      })),
      connectors: parseConnectorRequirements(prerequisites.connectors),
    };
  }

  // Legacy format: metadata.user_inputs.dialog[]
  const dialog = meta.user_inputs?.dialog;
  if (Array.isArray(dialog)) {
    const required: InputField[] = [];
    const optional: InputField[] = [];
    for (const d of dialog) {
      const field = normalizeInputField(d.required ?? false)(d);
      if (d.required) required.push(field);
      else optional.push(field);
    }
    return { ...empty, inputs_required: required, inputs_optional: optional, resources: [] };
  }

  return empty;
}

/**
 * Extract compaction directives from a SKILL.md markdown body.
 *
 * Scans the markdown body (not frontmatter) for a `## Compaction Directives` section
 * and returns each bullet point as a trimmed string. Compaction directives are hints
 * the agent uses to summarise context when the conversation history grows long.
 * Returns an empty array when the section is absent.
 *
 * @param content - Raw SKILL.md file content (full string including frontmatter and body)
 * @returns Array of directive strings extracted from the bullet list, or `[]` if none found
 *
 * @docLink packages/resolver/api-reference#parse-compaction-directives
 */
export function parseCompactionDirectives(content: string): string[] {
  const headerIdx = content.search(/^## Compaction Directives/m);
  if (headerIdx === -1) return [];

  // Header on the last line (no trailing newline) → no body to scan.
  const newlineIdx = content.indexOf("\n", headerIdx);
  if (newlineIdx === -1) return [];
  const bodyStart = newlineIdx + 1;
  const rest = content.slice(bodyStart);
  const nextSection = rest.search(/^##\s/m);
  const sectionBody = nextSection === -1 ? rest : rest.slice(0, nextSection);

  const bullets: string[] = [];
  for (const line of sectionBody.split("\n")) {
    const match = line.match(/^\s*[-*]\s+(.+)/);
    if (match) bullets.push(match[1].trim());
  }
  return bullets;
}

function normalizeInputField(defaultRequired: boolean) {
  return (d: any): InputField => ({
    // UserInputSchema permits `key` or `id`; `prompt` is the legacy label alias.
    id: d.id ?? d.key,
    label: d.label ?? d.prompt ?? d.id ?? d.key,
    type: d.type ?? "text",
    required: d.required ?? defaultRequired,
    ...(d.options ? { options: d.options } : {}),
    ...(d.default != null ? { default: d.default } : {}),
    ...(d.hint ? { hint: d.hint } : {}),
    ...(d.schema ? { schema: d.schema } : {}),
  });
}

function parseConnectorRequirements(raw: unknown): ConnectorRequirement[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => typeof c === "object" && c !== null)
    .map((c) => ({
      driver: String(c.driver ?? ""),
      ...(c.access === "read-write"
        ? { access: "read-write" as const }
        : { access: "read-only" as const }),
    }))
    .filter((c) => c.driver.length > 0);
}
